import React from "react";
// Real Time Dashboard page

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import RealTimeEnergyDataBarChart from "../charts/RealTimeEnergyDataBarChart";
import CustomDataTable from "../table/CustomTable";
import GridInteractionLineChart from "../charts/GridInteractionLineChart";

// React icons
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { MdOutlineProductionQuantityLimits } from "react-icons/md";
import { FaChartLine } from "react-icons/fa";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const RealTimeDashboard: React.FC = () => {
  const COLUMNS = ["Buying Quantity", "Price", "Total Value", "Details"];

  const realTimeEnergyData = [
    {
      buyingQuantity: "100 kWh",
      price: "₹4.50/kWh",
      totalValue: "₹450",
    },
    {
      buyingQuantity: "100 kWh",
      price: "₹4.50/kWh",
      totalValue: "₹450",
    },
    {
      buyingQuantity: "100 kWh",
      price: "₹4.50/kWh",
      totalValue: "₹450",
    },
    {
      buyingQuantity: "100 kWh",
      price: "₹4.50/kWh",
      totalValue: "₹450",
    },
    {
      buyingQuantity: "100 kWh",
      price: "₹4.50/kWh",
      totalValue: "₹450",
    },
  ];

  const realTimeDashboardData = [
    {
      title: "Energy Purchased",
      energy: "500",
      increament: "12.08",
      description: "Total energy bought this month.",
    },
    {
      title: "Energy Purchased",
      money: "1200",
      increament: "08.12",
      description: "Savings compared to standard grid rates",
    },
    {
      title: "Carbon Footprint Reduction",
      users: "1.2 tons CO2",
      increament: "08.12",
      description: "Equivalent to planting 50 trees.",
    },
  ];

  const rowsContent = realTimeEnergyData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.buyingQuantity}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.price}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.totalValue}
        </Typography>
      </TableCell>
      <TableCell>
        <ContainedButton
          label={"View Details"}
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
        />
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card>
        <CardHeader
          title={<Typography variant="h5">Real Time Dashboard</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {realTimeDashboardData?.map((item, index) => {
              return (
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <Card
                    sx={{
                      border: "1px solid #D7DAE7",
                      padding: "16px",
                      background: index === 0 ? "#0396FF" : "transparent",
                      height: 190,
                    }}
                  >
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"10px"}
                      >
                        <Box
                          sx={{
                            background: index === 0 ? "#28A6FF" : "#D7DAE7",
                            width: "fit-content",
                            padding: "10px 10px 3px 10px",
                            borderRadius: "5px",
                          }}
                        >
                          <UnifiedIcon
                            icon={MdOutlineProductionQuantityLimits}
                            color={index === 0 ? "#ffffff" : "#999b9d"}
                            fontSize="30px"
                          />
                        </Box>

                        <Typography
                          variant="h5"
                          color={index === 0 ? "#ffffff" : "#000000"}
                          fontWeight={500}
                          lineHeight={"25px"}
                        >
                          {item.title}
                        </Typography>
                      </Stack>

                      <UnifiedIcon
                        icon={HiOutlineDotsHorizontal}
                        color={index === 0 ? "#ffffff" : "#000000"}
                        fontSize="25px"
                      />
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      padding={"0 20px"}
                      mt={"20px"}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={500}
                        fontSize={"25px"}
                        color={index === 0 ? "#ffffff" : "#000000"}
                      >
                        {" "}
                        {item.money && <span>&#8377;</span>}{" "}
                        {item.users || item.energy || item.money}{" "}
                        {item.energy && (
                          <Typography fontSize={"12px"} fontWeight={400}>
                            kWh
                          </Typography>
                        )}{" "}
                      </Typography>

                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"7px"}
                      >
                        <UnifiedIcon
                          icon={FaChartLine}
                          color={index === 0 ? "#ffffff" : "#999B9D"}
                          fontSize="20px"
                        />
                        <Typography
                          variant="body1"
                          color={index === 0 ? "#ffffff" : "#000000"}
                        >
                          +{item.increament}%
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      mt={"20px"}
                    >
                      <Typography
                        variant="body1"
                        color={index === 0 ? "#ffffff" : "#000000"}
                        maxWidth={"180px"}
                        lineHeight={"20px"}
                      >
                        {item.description}
                      </Typography>
                      <ContainedButton
                        label={"Last Month"}
                        bgColor={
                          index === 0
                            ? "#ffffff"
                            : "linear-gradient(180deg, #1976d2 0%, #0396FF 100%)"
                        }
                        color={index === 0 ? "#0396FF" : "#ffffff"}
                        hoverBgColor={
                          index === 0
                            ? "#ffffff"
                            : "linear-gradient(180deg, #75D3FB 0%, #28A6FF 100%)"
                        }
                        sx={{
                          width: "fit-content",
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Real-Time Energy Data</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  maxHeight: "350px",
                }}
              >
                <CardContent>
                  <RealTimeEnergyDataBarChart />
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  maxHeight: "350px",
                }}
              >
                <CardContent>
                  <CustomDataTable
                    sx={{ maxHeight: "350px", pb: "35px" }}
                    columns={COLUMNS}
                    pagination={false}
                    rowsContent={rowsContent}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Grid Interaction</Typography>}
        />
        <Divider />

        <CardContent sx={{ height: "400px" }}>
          <GridInteractionLineChart />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default RealTimeDashboard;
